import Polyfill from "../Polyfill";
import {Add2CartButtonHandler} from "./Add2CartButtonHandler";
import {Events} from "./CartEvents";
import {CartConnectorButtonHandler} from "./CartConnectorButtonHandler";

class CartUrls {
    removeUrl:string = "";
    add2cartUrl:string = "";
    add2cartBulkUrl:string = "";
    getCartUrl:string = "";
    add2cartdialogurl:string = "";
    add2cartMultiDialogUrl:string = "";
    /** Joomla form token, sent with every request which changes the cart. */
    token:string = "";
}

/**
 * One image with one image type, as the bulk add expects it.
 */
export interface CartItemRequest {
    folder: string;
    file: string;
    imagetypeid: number;
    quantity: number;
}

/**
 * Totals of the cart as the server calculates them. The formatted strings
 * carry the currency the way the site shows it.
 */
export interface CartSummary {
    itemCount: number;
    lineItemCount: number;
    subtotal: string;
    subtotalAmount: number;
    total: string;
    totalAmount: number;
    currency: string;
}

/**
 * Outcome of a bulk add: entries which went in, images which did not.
 */
export interface BulkResult {
    added: number;
    failed: number;
}

export interface CartData {
    cart: any[];
    summary: CartSummary | null;
    bulk?: BulkResult;
}

export class CartProcessor {

    urls: CartUrls;
    private lastCartData: CartData | null = null;
    add2CartButtonHandler: Add2CartButtonHandler;
    cartConnectorButtonHandler: CartConnectorButtonHandler;

    /**
     * Number of requests currently on their way to the server.
     *
     * Changing a quantity sends the new cart to the server in the background.
     * Until that request has come back the server does not know about the
     * change, so following a link to the cart at that moment took the visitor
     * to a cart without the article: leaving the page cancels the request.
     *
     * Everything which navigates away therefore waits for this to reach zero.
     */
    private pendingRequests: number = 0;

    constructor() {
        // @ts-ignore
        this.urls = window.EventGalleryCartConfiguration as CartUrls || CartUrls;
        this.add2CartButtonHandler = new Add2CartButtonHandler(this, this.urls?.add2cartdialogurl);
        this.cartConnectorButtonHandler = new CartConnectorButtonHandler(this);
    }

    /**
     * Sets the quantity of one image type of one image.
     *
     * @param reference "folder=...&file=...&imagetypeid=..." as found in the data-id attributes
     * @param quantity the new quantity, 0 removes the item
     */
    add(reference: string, quantity:number): Promise<void> {
        return this.post(this.urls.add2cartUrl, new URLSearchParams(reference + '&quantity=' + quantity));
    }

    /**
     * Adds several images in one request. The quantities are added to what is
     * in the cart already.
     */
    addItems(items: CartItemRequest[]): Promise<void> {
        return this.post(this.urls.add2cartBulkUrl, new URLSearchParams({items: JSON.stringify(items)}));
    }

    remove(reference: string): Promise<void> {
        return this.post(this.urls.removeUrl, new URLSearchParams(reference));
    }

    loadCart(): Promise<void> {
        return this.request(this.urls.getCartUrl);
    }

    /**
     * True while at least one request has not come back yet.
     */
    isBusy(): boolean {
        return this.pendingRequests > 0;
    }

    /**
     * Sends a request which changes the cart. Those go as POST and carry the
     * form token, so a link alone cannot fill or empty a visitor's cart.
     */
    private post(url: string, body: URLSearchParams): Promise<void> {
        return this.request(url, {
            method: 'POST',
            body: body,
            headers: {
                'X-CSRF-Token': this.urls.token || ''
            }
        });
    }

    /**
     * Runs one request and keeps track of it.
     *
     * A failing request used to disappear without a trace: there was no error
     * handling at all, so a cart which never reached the server looked exactly
     * like one which did. It now reports the failure.
     */
    private request(url: string, init?: RequestInit): Promise<void> {
        this.pendingRequests++;
        this.dispatchBusy();

        const finished = () => {
            this.pendingRequests--;
            this.dispatchBusy();
        };

        return fetch(url, init)
            .then(response => {
                if (!response.ok) {
                    throw new Error('The cart request failed with HTTP ' + response.status);
                }
                return response.json();
            })
            .then(data => {
                this.dispatchCartData(data);
                finished();
            })
            .catch(error => {
                finished();
                this.dispatchError(error);
            });
    }

    /**
     * Hands a cart response to everybody listening for the reload event.
     *
     * The server answers with `{cart: [...], summary: {...}}`. An older answer
     * was the bare list of line items; that shape is still accepted so a page
     * with a cached script keeps working.
     */
    dispatchCartData(data: any) {
        const payload: CartData = Array.isArray(data) ? {cart: data, summary: null} : data;
        this.lastCartData = payload;
        document.dispatchEvent(Polyfill.createNewEvent(Events.reload, payload));
    }

    /**
     * The last cart the server sent, or null before the first response.
     */
    getLastCartData(): CartData | null {
        return this.lastCartData;
    }

    dispatchBusy() {
        document.dispatchEvent(Polyfill.createNewEvent(Events.busy, {busy: this.isBusy()}));
    }

    dispatchError(error: any) {
        // eslint-disable-next-line no-console
        console.error(error);
        document.dispatchEvent(Polyfill.createNewEvent(Events.error, {error: error}));
    }
}
